import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import Table from 'react-bootstrap/Table';
import { api } from '../../config/api';
import { statusRequiresInterviewDate } from '../../utils/candidateStatus';
import { submitStatusUpdate } from '../../utils/submitStatusUpdate';
import AppModal from '../ui/AppModal';
import StatusBadge from '../ui/StatusBadge';
import StatusSelect from '../ui/StatusSelect';

const PAGE_SIZE = 10;

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function RejectedProfileStatusCell({
  candidateId,
  currentStatus,
  editable,
  updatedBy,
  onUpdated,
}) {
  const savedStatus = currentStatus || 'No Action Taken';
  const [selectedStatus, setSelectedStatus] = useState(savedStatus);
  const [statusRemark, setStatusRemark] = useState('');
  const [statusInterviewDate, setStatusInterviewDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelectedStatus(savedStatus);
    setStatusRemark('');
    setStatusInterviewDate('');
  }, [savedStatus, candidateId]);

  const hasPendingChange = selectedStatus !== savedStatus;

  const handleStatusChange = (event) => {
    const nextStatus = event.target.value;
    setSelectedStatus(nextStatus);
    if (nextStatus === savedStatus) {
      setStatusRemark('');
      setStatusInterviewDate('');
    }
  };

  const handleCancel = () => {
    setSelectedStatus(savedStatus);
    setStatusRemark('');
    setStatusInterviewDate('');
  };

  const handleSave = async () => {
    if (!candidateId || !hasPendingChange) return;

    setSaving(true);
    try {
      await submitStatusUpdate({
        api,
        candidateId,
        selectedStatus,
        statusRemark,
        statusInterviewDate,
        updatedBy,
        onSuccess: (responseData) => {
          const newStatus = responseData?.status || selectedStatus;
          onUpdated?.(candidateId, newStatus, responseData);
          setStatusRemark('');
          setStatusInterviewDate('');
        },
      });
    } finally {
      setSaving(false);
    }
  };

  if (!editable) {
    return <StatusBadge status={savedStatus} useStoredLabel />;
  }

  return (
    <div className="hr-rejected-status-editor">
      <StatusSelect
        className="hr-rejected-status-select"
        value={selectedStatus || ''}
        onChange={handleStatusChange}
      />
      {hasPendingChange && (
        <div className="hr-rejected-status-save mt-2">
          {statusRequiresInterviewDate(selectedStatus) && (
            <Form.Control
              type="date"
              size="sm"
              className="mb-2"
              value={statusInterviewDate}
              onChange={(event) => setStatusInterviewDate(event.target.value)}
              required
            />
          )}
          <Form.Control
            as="textarea"
            rows={2}
            size="sm"
            className="mb-2"
            value={statusRemark}
            onChange={(event) => setStatusRemark(event.target.value)}
            placeholder="Remark for status update"
            required
          />
          <div className="d-flex gap-1">
            <Button
              variant="success"
              size="sm"
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={saving}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function RequirementRejectedModal({
  show,
  onHide,
  subtitle,
  rows = [],
  loading = false,
  editableStatus = false,
  updatedBy = '',
  onStatusUpdated,
}) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [localRows, setLocalRows] = useState(rows);

  useEffect(() => {
    if (show) {
      setSearch('');
      setPage(1);
    }
  }, [show]);

  useEffect(() => {
    setLocalRows(rows);
  }, [rows]);

  const handleRowStatusUpdated = (candidateId, newStatus, responseData) => {
    setLocalRows((prev) => prev.map((row) => (
      row.candidateId === candidateId
        ? { ...row, currentStatus: newStatus }
        : row
    )));
    onStatusUpdated?.(candidateId, newStatus, responseData);
  };

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return localRows;
    return localRows.filter((row) => [
      row.candidateName,
      row.uploadedByName,
      row.uploadedByRole,
      row.teamLeadName,
      row.rejectionStage,
      row.rejectedBy,
      row.rejectionReason,
      row.currentStatus,
    ].some((value) => String(value || '').toLowerCase().includes(query)));
  }, [localRows, search]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedRows = filteredRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  return (
    <AppModal
      show={show}
      onHide={onHide}
      title="Rejected Profiles"
      subtitle={subtitle}
      size="xl"
      fullscreen="lg-down"
    >
      <div className="hr-requirement-profiles-toolbar">
        <input
          type="search"
          className="form-control"
          placeholder="Search by candidate, uploader, stage, or reason"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
        />
      </div>

      {loading ? (
        <p className="hr-empty-state">Loading rejected profiles…</p>
      ) : filteredRows.length === 0 ? (
        <p className="hr-empty-state">No rejected profiles found for this requirement.</p>
      ) : (
        <>
          <p className="hr-page-hint mb-3">
            {filteredRows.length} rejected profile{filteredRows.length === 1 ? '' : 's'} shown.
          </p>
          <div className="table-responsive">
            <Table bordered hover responsive className="hr-table table-sm">
              <thead>
                <tr>
                  <th>Candidate Name</th>
                  <th>Uploaded By</th>
                  <th>Team Lead</th>
                  <th>Rejected Stage</th>
                  <th>Rejected Date</th>
                  <th>Rejected By</th>
                  <th>Rejection Reason</th>
                  <th style={{ minWidth: editableStatus ? '220px' : undefined }}>Current Status</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((row) => (
                  <tr key={row.candidateId || `${row.candidateName}-${row.rejectedDate}`}>
                    <td>{row.candidateName || '—'}</td>
                    <td>
                      <div><strong>{row.uploadedByName || row.recruiterName || '—'}</strong></div>
                      <div className="text-muted small">{row.uploadedByRole || '—'}</div>
                    </td>
                    <td>{row.teamLeadName || '—'}</td>
                    <td>{row.rejectionStage || '—'}</td>
                    <td>{formatDate(row.rejectedDate)}</td>
                    <td>{row.rejectedBy || '—'}</td>
                    <td>{row.rejectionReason || '—'}</td>
                    <td>
                      <RejectedProfileStatusCell
                        candidateId={row.candidateId}
                        currentStatus={row.currentStatus}
                        editable={editableStatus}
                        updatedBy={updatedBy}
                        onUpdated={handleRowStatusUpdated}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span className="hr-page-hint">
                Page {currentPage} of {totalPages}
              </span>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage <= 1}
                  onClick={() => setPage((value) => Math.max(1, value - 1))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </AppModal>
  );
}

export default RequirementRejectedModal;
